import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setCrmWalletEnabled,
  setMaxShops,
  setMaxBranchesPerShop,
} from "../../../redux/featureToggleSlice";
import { superAdminLogout } from "../../../redux/authRedux";
import { Switch, Typography, Box, TextField, Button, CircularProgress, Alert } from "@mui/material";
import axios from "axios";
import { API_URL } from "../../../api";

const SuperAdminSetting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const crmWalletEnabled = useSelector(
    (state) => state.featureToggle.crmWalletEnabled,
  );
  const storedMaxShops = useSelector((state) => state.featureToggle.maxShops);
  const storedMaxBranches = useSelector(
    (state) => state.featureToggle.maxBranchesPerShop,
  );

  const [maxShops, setMaxShopsLocal] = useState(storedMaxShops);
  const [maxBranches, setMaxBranchesLocal] = useState(storedMaxBranches);
  const [settingId, setSettingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingWallet, setSavingWallet] = useState(false);
  const [savingShops, setSavingShops] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const initialFetchDone = useRef(false);

  const getApi = () => {
    const token = localStorage.getItem("token");
    return axios.create({
      baseURL: API_URL(),
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  };

  const saveSetting = async (payload) => {
    const api = getApi();
    if (settingId) {
      const res = await api.patch(`admin-setting/${settingId}`, payload);
      return res.data;
    }
    const res = await api.post("admin-setting", payload);
    setSettingId(res.data.data._id);
    return res.data;
  };

  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    const fetchSettings = async () => {
      try {
        const api = getApi();
        const res = await api.get("admin-setting");
        const list = res.data.data;
        if (list && list.length > 0) {
          const setting = list[0];
          setSettingId(setting._id);
          dispatch(setCrmWalletEnabled(setting.isAccess ?? true));
          dispatch(setMaxShops(setting.maximunShop ?? 1));
          dispatch(setMaxBranchesPerShop(setting.minimunShop ?? 1));
          setMaxShopsLocal(setting.maximunShop ?? 1);
          setMaxBranchesLocal(setting.minimunShop ?? 1);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error("Failed to fetch admin settings:", err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [dispatch]);

  const handleWalletToggle = async (e) => {
    const checked = e.target.checked;
    dispatch(setCrmWalletEnabled(checked));
    setSavingWallet(true);
    setError(null);
    setSuccess(null);
    try {
      await saveSetting({ isAccess: checked });
      setSuccess("Wallet feature setting saved");
    } catch (err) {
      dispatch(setCrmWalletEnabled(!checked));
      setError(err.response?.data?.message || "Failed to save wallet setting");
    } finally {
      setSavingWallet(false);
    }
  };

  const handleSaveShops = async () => {
    setSavingShops(true);
    setError(null);
    setSuccess(null);
    try {
      await saveSetting({
        maximunShop: Number(maxShops),
        minimunShop: Number(maxBranches),
      });
      dispatch(setMaxShops(Number(maxShops)));
      dispatch(setMaxBranchesPerShop(Number(maxBranches)));
      setSuccess("Settings saved successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save settings");
    } finally {
      setSavingShops(false);
    }
  };

  const handleLogout = () => {
    dispatch(superAdminLogout());
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/category");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Super Admin Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
        <Typography variant="body1" fontWeight={500}>
          CRM and Wallet Feature
        </Typography>
        <Switch
          checked={crmWalletEnabled}
          onChange={handleWalletToggle}
          disabled={savingWallet}
        />
        {savingWallet && <CircularProgress size={16} />}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 4 }}>
        <TextField
          label="Max Shops Allowed"
          type="number"
          size="small"
          value={maxShops}
          onChange={(e) => setMaxShopsLocal(e.target.value)}
          inputProps={{ min: 1 }}
          sx={{ width: 200 }}
        />
        <TextField
          label="Max Branches Per Shop"
          type="number"
          size="small"
          value={maxBranches}
          onChange={(e) => setMaxBranchesLocal(e.target.value)}
          inputProps={{ min: 1 }}
          sx={{ width: 200 }}
        />
        <Button
          variant="contained"
          onClick={handleSaveShops}
          disabled={savingShops}
        >
          {savingShops ? <CircularProgress size={20} /> : "Save"}
        </Button>
        <Button variant="contained" sx={{ ml: "auto" }} onClick={handleLogout}>
          Logout
        </Button>
      </Box>
    </Box>
  );
};

export default SuperAdminSetting;
